import { useCallback, useRef } from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from 'react-native';
import type { ChatMessage } from '../../hooks/useChatWithPowerSync';
import { ChatMessage as ChatMessageComponent } from '~/chat/components/chat-message/ChatMessage';
import { TypingIndicator } from '~/chat/components/typing-indicator/TypingIndicator';

/** Treat the user as "at the bottom" within this many px. */
const BOTTOM_THRESHOLD = 50;

export interface ChatWindowProps {
  messages: ChatMessage[];
  /** Show the assistant typing indicator while awaiting a reply. */
  thinking?: boolean;
  /** Smaller message type for the right-rail chat. */
  compact?: boolean;
}

export const ChatWindow = ({
  messages,
  thinking,
  compact,
}: ChatWindowProps) => {
  const scrollRef = useRef<ScrollView>(null);
  // Whether new content should stick to the bottom. Defaults true so the first
  // content layout lands on the latest message. Refs (not state) so scroll
  // bookkeeping never triggers a re-render.
  const stickToBottomRef = useRef(true);
  const lastOffsetRef = useRef(0);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const y = contentOffset.y;
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - y;

      // Only the *user* scrolling up unsticks. Programmatic scroll-to-bottom
      // and content growing below the viewport both leave the offset
      // unchanged or increasing, so they never flip the flag off — this is
      // what stops the auto-scroll from giving up while content is still
      // rendering (async message parts, markdown reflow, the typing bubble).
      if (y < lastOffsetRef.current - 1) {
        stickToBottomRef.current = false;
      }
      // Reaching the bottom (manually or otherwise) re-arms sticky.
      if (distanceFromBottom <= BOTTOM_THRESHOLD) {
        stickToBottomRef.current = true;
      }
      lastOffsetRef.current = y;
    },
    [],
  );

  // Fires whenever content height changes — a new message, an async-loaded
  // message part, markdown reflow, or the typing bubble appearing. Defer to the
  // next frame so the layout is settled before we measure/scroll, then scroll
  // only if the user hasn't deliberately scrolled up.
  const handleContentSizeChange = useCallback(() => {
    if (!stickToBottomRef.current) return;
    requestAnimationFrame(() => {
      if (stickToBottomRef.current) {
        scrollRef.current?.scrollToEnd({ animated: true });
      }
    });
  }, []);

  return (
    <ScrollView
      ref={scrollRef}
      onScroll={handleScroll}
      scrollEventThrottle={16}
      onContentSizeChange={handleContentSizeChange}
      contentContainerClassName="flex-col items-start gap-y-4 p-4"
      className="flex-1"
    >
      {messages.map((message) => (
        <ChatMessageComponent.Synced
          message={message}
          key={message.id}
          compact={compact}
        />
      ))}
      {thinking && <TypingIndicator compact={compact} />}
    </ScrollView>
  );
};
